// Pure inventory presentation helpers for the owner Devices page. Keeps the
// source-name, filter, sort, and protocol-aware identity rules in one place so
// the controls and each row always describe the same device the same way.

import { seenStatus } from './deviceSeen';
import { humanizeGatewayName } from './networkRecommendation';
import type { VesselDevice } from './useBoatDevices';

export type DeviceSortKey = 'name' | 'manufacturer' | 'source';
export type DeviceStatusFilter = 'all' | 'online' | 'offline';

export interface DeviceInventoryFilters {
  manufacturer: string;
  source: string;
  status: DeviceStatusFilter;
}

export interface DeviceFilterOption {
  value: string;
  label: string;
}

export const ALL_MANUFACTURERS = 'all';
export const ALL_SOURCES = 'all';
export const UNKNOWN_MANUFACTURER = '__unknown_manufacturer__';
export const UNKNOWN_SOURCE = '__unknown_source__';

/**
 * Keep a live filter selection only while its option still exists. Device
 * inventory refreshes can remove the final device for a manufacturer/source;
 * treating that vanished key as "all" avoids a misleading empty inventory.
 */
export function availableFilterValue(
  value: string,
  allValue: string,
  options: DeviceFilterOption[]
): string {
  if (value === allValue || options.some((option) => option.value === value)) return value;
  return allValue;
}

/**
 * The gateway-source filter the Devices screen opens on: always every gateway.
 *
 * This used to seed from the boat's primary, on the reasoning that "each
 * gateway reports its own copy of the same devices, so all-gateways is the
 * duplicated view." That premise does not hold at this layer — the boat
 * collapses a device seen through several gateways onto one `wireDeviceId`
 * before it reaches `vessel_devices`, so "All gateways" is a list of distinct
 * devices, not duplicates.
 *
 * What the filter actually keys on is `sourceId`, the ONE gateway the collapse
 * recorded as a device's owner — not every gateway that can see it. On
 * Gallivant `can0` sees 57 devices but owns 18, so opening on the primary
 * showed 18 of 177 and read as a broken page. The primary is still marked
 * `· primary` in the option label; it is just no longer preselected.
 *
 * Kept as a named function rather than inlining `ALL_SOURCES` at the call site
 * so "what does this screen open on, and why" stays documented in one place.
 */
export function defaultSourceFilter(): string {
  return ALL_SOURCES;
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function deviceManufacturerName(device: VesselDevice): string {
  return device.manufacturer?.name?.trim() || 'Unknown manufacturer';
}

export function deviceManufacturerKey(device: VesselDevice): string {
  return device.manufacturer?.name?.trim() || UNKNOWN_MANUFACTURER;
}

export function deviceSourceKey(device: VesselDevice): string {
  return device.sourceId?.trim() || UNKNOWN_SOURCE;
}

function sourceKind(device: VesselDevice): string {
  if (device.protocol === 'n2k') return 'NMEA 2000 gateway';
  if (device.protocol === 'n0183') return 'NMEA 0183 source';
  if (device.protocol === 'venus') return 'Victron GX';
  if (device.protocol === 'starlink') return 'Starlink';
  return 'Signal K server';
}

function shortSourceId(sourceId: string): string {
  const compact = sourceId.replace(/^src_/i, '').replace(/[^a-z0-9]/gi, '');
  return compact.slice(0, 4).toUpperCase();
}

/**
 * Friendly gateway/feed name for a device. When source analysis has run, its
 * real gateway name wins. Older boats fall back to a protocol label plus a
 * short disambiguator rather than exposing the full opaque `src_…` id.
 */
export function deviceSourceName(
  device: VesselDevice,
  options: { includeConnection?: boolean } = {}
): string {
  const analyzed = device.sourceName?.trim();
  if (analyzed) {
    const friendly = humanizeGatewayName(analyzed);
    if (options.includeConnection) return friendly;
    return friendly.replace(/\s+\([^()]+\)$/, '');
  }

  const kind = sourceKind(device);
  const sourceId = device.sourceId?.trim();
  if (!sourceId) return `Unknown ${kind}`;
  const suffix = shortSourceId(sourceId);
  return suffix ? `${kind} · ${suffix}` : kind;
}

/** A readable, protocol-aware identity; raw CAN/internal ids stay secondary. */
export function deviceIdentityName(device: VesselDevice): string {
  if (device.protocol === 'n2k') {
    const manufacturer = device.manufacturer?.name?.trim();
    const model = device.productInfo?.modelId?.trim();
    const product = [manufacturer, model].filter(Boolean).join(' ') || device.displayName;
    const network =
      device.srcAddress === undefined
        ? 'NMEA 2000 device'
        : `NMEA 2000 address ${device.srcAddress}`;
    return `${product} · ${network}`;
  }
  if (device.protocol === 'n0183') {
    return device.talkerId ? `NMEA 0183 · talker ${device.talkerId}` : 'NMEA 0183 source';
  }
  if (device.protocol === 'venus') {
    const model = device.productInfo?.modelId?.trim();
    return `${model || device.displayName} · Victron GX`;
  }
  if (device.protocol === 'starlink') {
    const model = device.productInfo?.modelId?.trim();
    return `${model || device.displayName} · Starlink dish`;
  }
  return device.sourceLabel ? `Signal K · ${device.sourceLabel}` : 'Signal K device';
}

// Distinct value→label options for a filter dropdown, sorted alphabetically
// with the "unknown" bucket pinned last.
function filterOptions(
  devices: VesselDevice[],
  keyOf: (device: VesselDevice) => string,
  labelOf: (device: VesselDevice) => string,
  unknownKey: string
): DeviceFilterOption[] {
  const labels = new Map<string, string>();
  for (const device of devices) {
    labels.set(keyOf(device), labelOf(device));
  }
  return [...labels.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === unknownKey) return 1;
      if (b.value === unknownKey) return -1;
      return compareText(a.label, b.label);
    });
}

export function manufacturerFilterOptions(devices: VesselDevice[]): DeviceFilterOption[] {
  return filterOptions(
    devices,
    deviceManufacturerKey,
    deviceManufacturerName,
    UNKNOWN_MANUFACTURER
  );
}

export function sourceFilterOptions(devices: VesselDevice[]): DeviceFilterOption[] {
  return filterOptions(
    devices,
    deviceSourceKey,
    (device) =>
      deviceSourceKey(device) === UNKNOWN_SOURCE
        ? 'Unknown gateway / source'
        : deviceSourceName(device, { includeConnection: true }),
    UNKNOWN_SOURCE
  );
}

export function filterDevices(
  devices: VesselDevice[],
  filters: DeviceInventoryFilters,
  now: number
): VesselDevice[] {
  return devices.filter((device) => {
    if (
      filters.manufacturer !== ALL_MANUFACTURERS &&
      deviceManufacturerKey(device) !== filters.manufacturer
    ) {
      return false;
    }
    if (filters.source !== ALL_SOURCES && deviceSourceKey(device) !== filters.source) {
      return false;
    }
    if (filters.status !== 'all') {
      const state = seenStatus(device.lastSeen, now).state;
      const online = state === 'fresh' || state === 'stale';
      if (filters.status === 'online' ? !online : online) return false;
    }
    return true;
  });
}

export function sortDevices(devices: VesselDevice[], sort: DeviceSortKey): VesselDevice[] {
  // Decorate each device with its sort key once: the manufacturer/source names
  // run regexes and humanization, and a comparator is invoked O(n·log n) times,
  // so computing the key inside it would re-derive each device's label on every
  // comparison. `unknown` pins the unknown bucket last for grouped sorts.
  const decorated = devices.map((device) => {
    if (sort === 'manufacturer') {
      return {
        device,
        unknown: deviceManufacturerKey(device) === UNKNOWN_MANUFACTURER,
        label: deviceManufacturerName(device),
      };
    }
    if (sort === 'source') {
      return {
        device,
        unknown: deviceSourceKey(device) === UNKNOWN_SOURCE,
        label: deviceSourceName(device, { includeConnection: true }),
      };
    }
    return { device, unknown: false, label: device.displayName };
  });
  decorated.sort(
    (a, b) =>
      (a.unknown === b.unknown ? 0 : a.unknown ? 1 : -1) ||
      compareText(a.label, b.label) ||
      compareText(a.device.displayName, b.device.displayName) ||
      compareText(a.device.deviceId, b.device.deviceId)
  );
  return decorated.map((entry) => entry.device);
}
